<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  agent: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const conversationsTo = ref('');
const dealsTo = ref('');
const tasksTo = ref('');
const showSecondaryConfirm = ref(false);

const transferTargets = computed(() =>
  props.agents.filter(user => user.id !== props.agent.id)
);

const allEmpty = computed(
  () => !conversationsTo.value && !dealsTo.value && !tasksTo.value
);

const secondaryTitle = computed(() =>
  allEmpty.value
    ? t('AGENT_MGMT.DELETE.CONFIRM.NO_TARGETS_TITLE')
    : t('AGENT_MGMT.DELETE.CONFIRM.WITH_TARGETS_TITLE')
);

const secondaryMessage = computed(() =>
  allEmpty.value
    ? t('AGENT_MGMT.DELETE.CONFIRM.NO_TARGETS_WARNING')
    : t('AGENT_MGMT.DELETE.CONFIRM.WITH_TARGETS_WARNING', {
        name: props.agent.name,
      })
);

watch(
  () => props.show,
  value => {
    if (!value) {
      conversationsTo.value = '';
      dealsTo.value = '';
      tasksTo.value = '';
      showSecondaryConfirm.value = false;
    }
  }
);

const openSecondaryConfirm = () => {
  showSecondaryConfirm.value = true;
};

const closeSecondaryConfirm = () => {
  showSecondaryConfirm.value = false;
};

const confirmDelete = () => {
  showSecondaryConfirm.value = false;
  emit('confirm', {
    transfer_conversations_to: conversationsTo.value || null,
    transfer_deals_to: dealsTo.value || null,
    transfer_tasks_to: tasksTo.value || null,
  });
};
</script>

<template>
  <woot-modal :show="props.show" :on-close="() => emit('close')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      />
      <div class="px-8 pt-2 pb-6">
        <p class="w-full text-n-slate-11 text-body-main leading-relaxed">
          {{
            $t('AGENT_MGMT.DELETE.CONFIRM.CONTEXT', { name: props.agent.name })
          }}
        </p>
      </div>

      <form
        class="flex flex-col items-start w-full gap-4 px-8 pb-4"
        @submit.prevent="openSecondaryConfirm"
      >
        <div class="w-full">
          <label>
            {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.CONVERSATIONS_LABEL') }}
            <select v-model="conversationsTo">
              <option value="">
                {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.KEEP_UNASSIGNED') }}
              </option>
              <option
                v-for="user in transferTargets"
                :key="`conv-${user.id}`"
                :value="user.id"
              >
                {{ user.name }}
              </option>
            </select>
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.DEALS_LABEL') }}
            <select v-model="dealsTo">
              <option value="">
                {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.KEEP_WITHOUT_OWNER') }}
              </option>
              <option
                v-for="user in transferTargets"
                :key="`deal-${user.id}`"
                :value="user.id"
              >
                {{ user.name }}
              </option>
            </select>
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.TASKS_LABEL') }}
            <select v-model="tasksTo">
              <option value="">
                {{ $t('AGENT_MGMT.DELETE.CONFIRM.FIELDS.KEEP_WITHOUT_OWNER') }}
              </option>
              <option
                v-for="user in transferTargets"
                :key="`task-${user.id}`"
                :value="user.id"
              >
                {{ user.name }}
              </option>
            </select>
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.DELETE.CONFIRM.CANCEL_BUTTON')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            ruby
            :label="$t('AGENT_MGMT.DELETE.CONFIRM.CONFIRM_BUTTON')"
          />
        </div>
      </form>
    </div>
  </woot-modal>

  <woot-modal
    :show="showSecondaryConfirm"
    :on-close="closeSecondaryConfirm"
    size="modal-small"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="secondaryTitle" />
      <div class="px-8 pt-2 pb-6">
        <p class="w-full text-n-slate-11 text-body-main leading-relaxed">
          {{ secondaryMessage }}
        </p>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-8 pb-4">
        <Button
          faded
          slate
          :label="$t('AGENT_MGMT.DELETE.CONFIRM.CANCEL_BUTTON')"
          @click="closeSecondaryConfirm"
        />
        <Button
          ruby
          :label="
            $t('AGENT_MGMT.DELETE.CONFIRM.CONFIRM_WITHOUT_TRANSFER_BUTTON')
          "
          @click="confirmDelete"
        />
      </div>
    </div>
  </woot-modal>
</template>
